import React from 'react';
import SquarePaymentForm from '../intake/SquarePaymentForm';
import { ProviderReviewNotice } from '../intake/checkoutUi';
import {
  REFILL_DOSE_ID, REFILL_PHARMACY_ID,
  doseBlockFrom, fmtUsd, isConsultOnly, isConsultLeg, isLocalLeg, isChange, manufacturerBrand, manufacturerOption, orderedLabel, prescribedLabel,
  paymentExtrasFrom, quoteLineItems,
} from '@/lib/followupQuote';

// §branded-refill §2.3 — checkout driven by GET /followup/quote.
//
// Rendered in place of the plan-picker CheckoutStep when the quote prices the
// refill as consult-only (the patient's own pharmacy bills the medication).
// Every number on this screen comes from the quote; the payment call carries
// quoteId + the quote's visitType + selectedMedIds:[quote.medId] and, on the
// local leg, prescriptionFulfillment:'local' + pharmacySelection. The BE
// charges the stored quote's totalCents — never an FE amount.
//
// `staleNotice` is set by the parent after a 409 quote_stale on the payment
// call: nothing was charged, the quote was refreshed, confirm again.
//
// Layout: ONE column, max-w-md, like the new-visit catalog checkout. The
// follow-up renders inside the dashboard's Refill dialog (an iframe ~900px
// wide, and the modal is ~420px on phones); the two-column grid clipped the
// payment column there (owner screenshot 2026-10-07 22:15 ET).
const RefillQuoteCheckout = ({
  quote, answers, onComplete, isProcessing, setLoading, email, currentStep, timestamp,
  shipTo, paymentHeld, onQuoteStale, staleNotice, requoting, stagingBypass, modalityState,
}) => {
  if (!quote) return <div data-testid="refill-checkout" className="max-w-4xl mx-auto" />;

  // Everything below renders from the CURRENT quote — after a pick the parent
  // re-quoted with requestedMedId, so medId / totalCents / medicationCents are
  // the pick's. Never the first quote's numbers once a change is picked.
  const doseAnswer = answers?.[REFILL_DOSE_ID];
  const lines = quoteLineItems(quote, doseAnswer);
  const total = Number(quote.totalCents) || 0;
  const dose = doseBlockFrom(doseAnswer);
  const changed = isChange(quote);
  const ordered = orderedLabel(quote, doseAnswer);
  const prescribed = prescribedLabel(quote);
  const pharmacy = answers?.[REFILL_PHARMACY_ID] || null;
  const local = isLocalLeg(quote);
  const consultLeg = isConsultLeg(quote); // local OR manufacturer: nothing ships from us
  const mfr = manufacturerOption(quote);
  const extras = paymentExtrasFrom(quote, answers || {});

  const pharmacyLine = pharmacy && pharmacy.choice === 'manufacturer' && mfr
    ? `${mfr.name}${manufacturerBrand(quote) ? ` (${manufacturerBrand(quote)}'s manufacturer pharmacy)` : ''}`
    : !consultLeg
    ? 'Ships from our partner pharmacy.'
    : pharmacy && pharmacy.choice === 'different' && pharmacy.pharmacy
      ? `${pharmacy.pharmacy.storeName || 'Your chosen pharmacy'}${[pharmacy.pharmacy.city, pharmacy.pharmacy.state].filter(Boolean).length ? ` · ${[pharmacy.pharmacy.city, pharmacy.pharmacy.state].filter(Boolean).join(', ')}` : ''}`
      : pharmacy && pharmacy.choice === 'same'
        ? (pharmacy.pharmacy && pharmacy.pharmacy.storeName) ? `${pharmacy.pharmacy.storeName} (your current pharmacy)` : 'Your current pharmacy'
        : 'Your pharmacy';

  return (
    <div data-testid="refill-checkout" className="max-w-md mx-auto min-w-0">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">Review your refill and pay</h2>
        <p className="text-gray-500">
          {isConsultOnly(quote)
            ? 'Your clinician reviews your refill. Your pharmacy fills and bills the medication.'
            : 'Confirm your refill below.'}
        </p>
      </div>

      <div className="space-y-4 min-w-0">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 min-w-0" data-testid="refill-summary">
          <h3 className="font-semibold text-gray-900 mb-3">Your refill</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500 flex-shrink-0">Medication</dt>
              {/* What THIS quote orders: the pick after a change, else the Rx. */}
              <dd className="text-gray-900 text-right min-w-0" data-testid="refill-medication">{ordered}</dd>
            </div>
            {(changed || dose.choice === 'change') && (
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500 flex-shrink-0">Requested</dt>
                <dd className="text-gray-900 text-right min-w-0" data-testid="refill-dose-requested">
                  {ordered} <span className="text-gray-500">(replaces {prescribed})</span>
                  <span className="block text-xs text-gray-400">Your clinician confirms the change.</span>
                </dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500 flex-shrink-0">Pharmacy</dt>
              <dd className="text-gray-900 text-right min-w-0" data-testid="refill-pharmacy-line">{pharmacyLine}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-[#f0faf4] rounded-2xl p-5 min-w-0" data-testid="refill-lines">
          {requoting && (
            <p className="text-sm text-gray-600 mb-2" data-testid="refill-lines-updating">Updating your price…</p>
          )}
          {!requoting && lines.map((l) => (
            <div key={l.key} className="mb-2">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-700">{l.label}</span>
                <span className="font-semibold text-gray-900 flex-shrink-0" data-testid={`refill-line-${l.key}`}>{fmtUsd(l.cents)}</span>
              </div>
              {l.note && <p className="text-xs text-gray-500">{l.note}</p>}
            </div>
          ))}
          <div className="mt-3 pt-3 border-t border-[#2d8a5f]/20 flex justify-between items-center gap-3">
            <span className="font-semibold text-lg">Total due today</span>
            <span className="text-2xl font-bold text-[#2d8a5f] flex-shrink-0" data-testid="refill-total">{requoting ? '…' : fmtUsd(total)}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 min-w-0">
          <ProviderReviewNotice className="mb-4" />
          {/* Ship-to only matters when something ships; a local-pharmacy refill is
              picked up at the pharmacy. */}
          {!consultLeg && shipTo}
          {staleNotice && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="refill-stale-notice">
              {staleNotice}
            </p>
          )}
          <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
          {paymentHeld ? (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="payment-held">
              Save or cancel your shipping address change to continue to payment.
            </p>
          ) : requoting ? (
            <p className="text-sm text-gray-600" data-testid="refill-requoting">Updating your price…</p>
          ) : stagingBypass ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                ⚠️ Staging sign-off mode — Square is bypassed. Submits a test <strong>{quote.visitType}</strong> refill with no charge.
              </p>
              <button
                type="button"
                data-testid="staging-bypass-pay"
                disabled={isProcessing}
                onClick={() => onComplete({ paymentId: 'STAGING_TEST_BYPASS' })}
                className="w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white disabled:opacity-50"
              >
                {isProcessing ? 'Submitting…' : '[Staging test] Skip payment & submit refill'}
              </button>
            </div>
          ) : (
            <div className="min-w-0 w-full">
              <SquarePaymentForm
                modalityVisitType={quote?.visitType}
                modalityState={modalityState}
                amount={total}
                onComplete={onComplete}
                answers={answers}
                setLoading={setLoading}
                clearSavedProgress={() => {}}
                primaryGoal="followup"
                currentStep={currentStep}
                timestamp={timestamp}
                email={email}
                selectedMedIds={extras.selectedMedIds}
                prescriptionFulfillment={extras.prescriptionFulfillment}
                pharmacySelection={extras.pharmacySelection}
                quoteId={extras.quoteId}
                visitType={extras.visitType}
                consultFeeVisitType={quote.visitType}
                onQuoteStale={onQuoteStale}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefillQuoteCheckout;
